// Loan maths: EMI, amortisation with prepayments, per-loan and portfolio summaries.
// Months are represented as integer keys (year * 12 + monthIndex) so arithmetic
// across years is plain addition.

export const toMonthKey = (date = new Date()) => date.getFullYear() * 12 + date.getMonth();

export const ymToKey = (ym) => {
  if (!ym || typeof ym !== "string") return toMonthKey();
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return toMonthKey();
  return y * 12 + (m - 1);
};

export const keyToYm = (key) => {
  const y = Math.floor(key / 12);
  const m = key - y * 12 + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
};

export const currentYm = () => keyToYm(toMonthKey());

/** Standard reducing-balance EMI. */
export function calcEmi(principal, annualRate, months) {
  const P = Number(principal) || 0;
  const n = Math.max(1, Math.round(Number(months) || 0));
  const r = (Number(annualRate) || 0) / 1200;
  if (P <= 0) return 0;
  if (r === 0) return P / n;
  const f = Math.pow(1 + r, n);
  return (P * r * f) / (f - 1);
}

export function totalInterestFor(principal, annualRate, months) {
  const emi = calcEmi(principal, annualRate, months);
  return emi * Math.max(1, Math.round(months)) - principal;
}

export const EMPTY_STRATEGY = Object.freeze({
  extraMonthly: 0,
  extraFromYm: null,
  annualExtra: { amount: 0, month: 3 },
  lumpSums: [],
});

export function normalizeStrategy(strategy) {
  const s = strategy || {};
  return {
    extraMonthly: Math.max(0, Number(s.extraMonthly) || 0),
    extraFromYm: s.extraFromYm || null,
    annualExtra: {
      amount: Math.max(0, Number(s.annualExtra?.amount) || 0),
      month: Math.min(12, Math.max(1, Number(s.annualExtra?.month) || 3)),
    },
    lumpSums: Array.isArray(s.lumpSums)
      ? s.lumpSums
          .filter((l) => l && l.ym && Number(l.amount) > 0)
          .map((l) => ({ id: l.id, ym: l.ym, amount: Number(l.amount) }))
      : [],
  };
}

export function strategyIsActive(strategy) {
  const s = normalizeStrategy(strategy);
  return s.extraMonthly > 0 || s.annualExtra.amount > 0 || s.lumpSums.length > 0;
}

/**
 * Month-by-month schedule. Prepayments reduce tenure (EMI stays the same),
 * which is how most Indian lenders apply part-prepayments by default.
 */
export function amortize(loan, { strategy, withStrategy = true, additional } = {}) {
  const principal = Math.max(0, Number(loan.principal) || 0);
  const rate = Math.max(0, Number(loan.annualRate) || 0);
  const n = Math.max(1, Math.round(Number(loan.tenureMonths) || 1));
  const startKey = ymToKey(loan.startYm);
  const emi = calcEmi(principal, rate, n);
  const r = rate / 1200;

  const s = normalizeStrategy(withStrategy ? strategy || loan.strategy : null);
  const extraFromKey = s.extraFromYm ? ymToKey(s.extraFromYm) : startKey;
  const lumps = new Map();
  for (const l of s.lumpSums) {
    const k = ymToKey(l.ym);
    lumps.set(k, (lumps.get(k) || 0) + l.amount);
  }

  // One-off "what if" payments layered on top of the saved plan (used by comparisons).
  const add = additional || {};
  const addMonthly = Math.max(0, Number(add.monthly) || 0);
  const addFromKey = Number.isFinite(add.fromKey) ? add.fromKey : startKey;
  if (Number(add.lump) > 0 && Number.isFinite(add.lumpKey)) {
    lumps.set(add.lumpKey, (lumps.get(add.lumpKey) || 0) + Number(add.lump));
  }

  const rows = [];
  let bal = principal;
  let totalInterest = 0;
  let totalExtra = 0;

  for (let i = 0; i < n && bal > 0.005; i++) {
    const key = startKey + i;
    const opening = bal;
    const interest = bal * r;
    let princ = emi - interest;
    if (i === n - 1 || princ > bal) princ = bal;
    bal -= princ;

    let extra = 0;
    if (bal > 0.005) {
      let planned = lumps.get(key) || 0;
      if (key >= addFromKey) planned += addMonthly;
      if (key >= extraFromKey) {
        planned += s.extraMonthly;
        if (s.annualExtra.amount > 0 && key % 12 === s.annualExtra.month - 1) {
          planned += s.annualExtra.amount;
        }
      }
      extra = Math.min(planned, bal);
      bal -= extra;
    }
    if (bal < 0.005) bal = 0;

    totalInterest += interest;
    totalExtra += extra;
    rows.push({
      i,
      key,
      opening,
      payment: princ + interest,
      interest,
      principal: princ,
      extra,
      closing: bal,
    });
  }

  return {
    emi,
    rows,
    totalInterest,
    totalExtra,
    startKey,
    endKey: rows.length ? rows[rows.length - 1].key : startKey,
    months: rows.length,
  };
}

/** Balance at the start of month `key` (before that month's EMI). */
export function balanceAt(schedule, principal, key) {
  const idx = key - schedule.startKey;
  if (idx <= 0) return principal;
  if (idx >= schedule.rows.length) return 0;
  return schedule.rows[idx].opening;
}

/** Everything the UI needs to know about one loan, as of `nowKey`. */
export function summarizeLoan(loan, nowKey = toMonthKey(), strategyOverride) {
  const principal = Math.max(0, Number(loan.principal) || 0);
  const standard = amortize(loan, { withStrategy: false });
  const plan = amortize(loan, { strategy: strategyOverride, withStrategy: true });

  const paidCount = Math.min(Math.max(nowKey - plan.startKey, 0), plan.rows.length);
  const outstanding = paidCount >= plan.rows.length ? 0 : plan.rows[paidCount].opening;

  let paidInterest = 0;
  for (let i = 0; i < paidCount; i++) paidInterest += plan.rows[i].interest;

  const remainingInterest = Math.max(0, plan.totalInterest - paidInterest);
  const remainingMonths = Math.max(0, plan.rows.length - paidCount);
  const current = plan.rows[paidCount] || null;

  let status = "active";
  if (nowKey < plan.startKey) status = "upcoming";
  else if (remainingMonths === 0) status = "closed";

  // Interest share of the next 12 contractual EMIs (shows how front-loaded interest is).
  let next12Interest = 0;
  let next12Paid = 0;
  for (let i = paidCount; i < Math.min(paidCount + 12, plan.rows.length); i++) {
    next12Interest += plan.rows[i].interest;
    next12Paid += plan.rows[i].interest + plan.rows[i].principal;
  }

  const hasStrategy = plan.totalExtra > 0.5;

  return {
    id: loan.id,
    loan,
    emi: standard.emi,
    standard,
    plan,
    status,
    outstanding,
    paidPrincipal: principal - outstanding,
    paidInterest,
    paidCount,
    remainingInterest,
    remainingMonths,
    current,
    progress: principal > 0 ? (principal - outstanding) / principal : 0,
    closeKey: plan.endKey,
    closeKeyStandard: standard.endKey,
    hasStrategy,
    interestSaved: Math.max(0, standard.totalInterest - plan.totalInterest),
    monthsSaved: Math.max(0, standard.months - plan.months),
    next12InterestShare: next12Paid > 0 ? next12Interest / next12Paid : 0,
  };
}

/** Totals across loans. Upcoming loans count towards commitments; closed ones don't. */
export function summarizePortfolio(loans, nowKey = toMonthKey()) {
  const items = loans.map((l) => summarizeLoan(l, nowKey));
  const open = items.filter((s) => s.status !== "closed");

  const sum = (arr, f) => arr.reduce((acc, x) => acc + f(x), 0);
  const outstanding = sum(open, (s) => s.outstanding);
  const totalPrincipal = sum(items, (s) => Number(s.loan.principal) || 0);
  const paidPrincipal = sum(items, (s) => s.paidPrincipal);

  const debtFreeKey = open.length ? Math.max(...open.map((s) => s.closeKey)) : null;
  const debtFreeKeyStandard = open.length ? Math.max(...open.map((s) => s.closeKeyStandard)) : null;

  const weightedRate =
    outstanding > 0
      ? sum(open, (s) => s.outstanding * (Number(s.loan.annualRate) || 0)) / outstanding
      : 0;

  const thisMonthInterest = sum(open, (s) => (s.current ? s.current.interest : 0));
  const thisMonthPrincipal = sum(open, (s) => (s.current ? s.current.principal + s.current.extra : 0));

  return {
    items,
    open,
    count: loans.length,
    activeCount: open.length,
    outstanding,
    totalPrincipal,
    paidPrincipal,
    progress: totalPrincipal > 0 ? paidPrincipal / totalPrincipal : 0,
    emi: sum(open, (s) => s.emi),
    remainingInterest: sum(open, (s) => s.remainingInterest),
    interestSaved: sum(items, (s) => s.interestSaved),
    hasStrategy: items.some((s) => s.hasStrategy),
    debtFreeKey,
    debtFreeKeyStandard,
    weightedRate,
    thisMonthInterest,
    thisMonthPrincipal,
  };
}

/**
 * Outstanding balance per month across a set of loans, for the standard schedule
 * and the prepayment plan. Returns [{ key, standard, plan }].
 */
export function balanceTimeline(summaries, fromKey, toKey) {
  const points = [];
  for (let k = fromKey; k <= toKey; k++) {
    let standard = 0;
    let plan = 0;
    for (const s of summaries) {
      const p = Number(s.loan.principal) || 0;
      standard += balanceAt(s.standard, p, k);
      plan += balanceAt(s.plan, p, k);
    }
    points.push({ key: k, standard, plan });
  }
  return points;
}

/** Aggregate a schedule into calendar years. */
export function yearlyBreakdown(schedule) {
  const years = new Map();
  for (const r of schedule.rows) {
    const y = Math.floor(r.key / 12);
    if (!years.has(y)) {
      years.set(y, {
        year: y,
        opening: r.opening,
        principal: 0,
        interest: 0,
        extra: 0,
        payment: 0,
        closing: r.closing,
        months: 0,
      });
    }
    const row = years.get(y);
    row.principal += r.principal;
    row.interest += r.interest;
    row.extra += r.extra;
    row.payment += r.payment + r.extra;
    row.closing = r.closing;
    row.months += 1;
  }
  return Array.from(years.values());
}

/** EMIs due in the next `count` months across loans, soonest first. */
export function upcomingPayments(summaries, nowKey, count = 3) {
  const out = [];
  for (let k = nowKey; k < nowKey + count; k++) {
    const lines = [];
    for (const s of summaries) {
      const idx = k - s.plan.startKey;
      if (idx < 0 || idx >= s.plan.rows.length) continue;
      const row = s.plan.rows[idx];
      lines.push({
        loanId: s.id,
        name: s.loan.name,
        colorIndex: s.loan.colorIndex,
        emi: row.payment,
        extra: row.extra,
        interest: row.interest,
        principal: row.principal,
      });
    }
    if (lines.length) {
      out.push({
        key: k,
        lines,
        total: lines.reduce((a, l) => a + l.emi + l.extra, 0),
      });
    }
  }
  return out;
}

/** Pick x-axis ticks for a month-key range: quarterly, yearly, or every N years. */
export function monthTicks(fromKey, toKey) {
  const span = toKey - fromKey;
  const ticks = [];
  if (span <= 18) {
    for (let k = fromKey; k <= toKey; k++) if (k % 3 === 0) ticks.push(k);
    return ticks;
  }
  const years = span / 12;
  const step = years <= 7 ? 1 : years <= 14 ? 2 : years <= 28 ? 5 : 10;
  const firstYear = Math.ceil(fromKey / 12);
  for (let y = firstYear; y * 12 <= toKey; y++) {
    if (y % step === 0) ticks.push(y * 12);
  }
  return ticks;
}

export function roundTo(value, step) {
  if (!step) return Math.round(value);
  return Math.round(value / step) * step;
}
